import './MainMenu.less';

import clsx from 'clsx';
import { dispatch, useReframe } from 'nike-re-framejs';
import { Button } from 'nike-ui/src/components/button/Button';
import React from 'react';

import { fireGAEvent } from '@/analytics';
import Languages from '@/common/components/language/Languages';
import { getConfigProperty, getDevice } from '@/common/oldMobile';
import i18n from '@i18n';
import AccountIcon from 'nike-ui/src/icons/svg/account-icon-sv.svg';
import AndroidIcon from 'nike-ui/src/icons/svg/android-icon-sv.svg';
import AppleIcon from 'nike-ui/src/icons/svg/apple-icon-sv.svg';
import BellActiveIcon from '@/icons/BellActiveIcon';
import BonusIcon from '@/icons/BonusIcon';
import FavoriteIcon from '@/icons/FavoriteIcon';
import HistoryIcon from 'nike-ui/src/icons/svg/history-icon-sv.svg';
import MessageIcon from '@/icons/MessageIcon';
import NIcon from '@/icons/NIcon';
import PolexIcon from 'nike-ui/src/icons/svg/polex-icon-sv.svg';
import ResultsIcon from '@/icons/ResultsIcon';
import SpinIcon from '@/icons/SpinIcon';
import TicketControllIcon from 'nike-ui/src/icons/svg/ticket-control-icon-sv.svg';
import UserProfilIcon from 'nike-ui/src/icons/svg/user-profil-icon-sv.svg';
import { browserInfo } from '@/notifications/web-notification-browser';
import { navigate } from '@/routing';

import {
    ACCOUNT_PROFILE,
    ACCOUNT_SETTINGS,
    APPLICATION_DOWNLOAD,
    BONUS_DASHBOARD_CURRENT,
    CASINO_HISTORY,
    CLUB_NIKE,
    I_SCRATCH_CARDS_HISTORY,
    MESSAGES,
    MONEY_DEPOSIT,
    NIKE1_FAV,
    NIKE1_RESULTS,
    NOTIFIED_MATCHES,
    ONB_POST_REGISTRATION,
    POLEX_DOWNLOAD,
    TICKET_CHECK,
    TOURNAMENTS,
    TRANSACTION_HISTORY
} from '@/routing/routes';
import UserMenuInfo from './UserMenuInfo';
import UserAnnouncement from '@/menu/views/UserAnnouncement';
import FinishRegistration from '@/home/views/FinishRegistration';
import RvoStatus from '@/rvo/RvoStatus';
import PopupNg from '@/ui/PopupNg';
import GearWheelIcon from 'nike-ui/src/icons/svg/gear-wheel-icon-sv.svg';
import { accountUnconfirmedPolex$, accountUserData$ } from '@/account/subs';
import { unseenTournamentCount$ } from '@/tournaments/subs';
import TournamentCupIcon from '@/icons/TournamentCupIcon';
import LogOutIcon from '@/icons/LogOutIcon';
import { isProfileIScratchCardEnabled, isProfileOlympiaEnabled } from 'nike-ui/src/profiles';
import { accountNewMessages$ } from '@/message/subs';

function MainMenu() {
    const { derefSub } = useReframe();
    const userData = derefSub(accountUserData$);

    return (
        <>
            <UserMenuInfo userData={userData} />
            <NikeLinks userData={userData} />
        </>
    );
}

function MenuLinkDownloadApp() {
    const device = getDevice();
    const appleUrl = getConfigProperty('get.app.ios');

    function onIosClick(e) {
        e.preventDefault();
        e.stopPropagation();
        window.open(appleUrl, '_blank');
    }

    const props =
        device === 'android'
            ? { icon: AndroidIcon, atid: 'main-menu-app-android', route: APPLICATION_DOWNLOAD }
            : { icon: AppleIcon, atid: 'main-menu-app-ios', onClick: onIosClick };

    return <MenuLink trackName="Aaplikacia" label={i18n('menu.aplikacia')} {...props} />;
}

function MenuLink({ atid, badge, children, disabled, icon: Icon, label, onClick, route, trackName }) {
    function handleClick(e) {
        if (!disabled) {
            fireGAEvent('Osobna zona', trackName);

            if (onClick) {
                onClick(e);
            }

            if (!e.defaultPrevented && e.button === 0) {
                e.preventDefault();
                navigate(route);
            }
        } else {
            dispatch(['login.openScreen']);
        }
    }

    return (
        <div className={clsx('menu-link', { disabled })} onClick={handleClick} data-atid={atid}>
            <Icon />

            <p>
                {label}
                {badge ? (
                    <span className="badge" data-atid="main-menu-badge">
                        {badge}
                    </span>
                ) : null}
                {children}
            </p>
        </div>
    );
}

function NotificationsMenuLink({ disabled }) {
    if (!browserInfo().browserSupported) {
        return null;
    }

    function onClick() {
        if (disabled) {
            dispatch(['login.openScreen']);
        } else {
            fireGAEvent('Osobna zona', 'Upozornenia');
            navigate(NOTIFIED_MATCHES);
        }
    }

    return (
        <div className={clsx('menu-link', { disabled })} onClick={onClick} data-atid="main-menu-notifikacie">
            <BellActiveIcon />
            <p>{i18n('menu.notifikacie')}</p>
        </div>
    );
}

function PolexStatus() {
    const derefSub = useReframe();
    const unconfirmedPolex = derefSub(accountUnconfirmedPolex$);

    return unconfirmedPolex ? (
        <UserAnnouncement
            icon={PolexIcon}
            title={i18n('polex.download.header')}
            description={i18n('polex.download.infotext')}
            lines={'max-lines-4'}
        >
            <Button className="red" onClick={downloadPolex} label={i18n('polex.download.button')} primary />
        </UserAnnouncement>
    ) : null;
}

function quickAction(label, action) {
    fireGAEvent('Nastavenia', 'Rychla akcia', label);
    action();
}

export const uploadDocumentsAction = () => quickAction('Nahranie dokumentov', () => navigate(ONB_POST_REGISTRATION));
const downloadPolex = () => quickAction('Polex', () => navigate(POLEX_DOWNLOAD));

function MenuStatuses() {
    return (
        <div className="announcements">
            <FinishRegistration />
            <RvoStatus />
            <PolexStatus />
        </div>
    );
}

function logout(e) {
    e.preventDefault();
    fireGAEvent('Nastavenia', 'Odhlasenie');
    PopupNg.dispatcher.open('account/logout');
}

export function NikeLinks({ userData }) {
    const { subscribe } = useReframe();
    const loggedIn = !!userData.get('cisloZmluvy');
    const unseenTournamentsCount = subscribe(unseenTournamentCount$);
    const sumBonuses = userData.getIn(['bonuses', 'activeCount'], 0) + userData.getIn(['bonuses', 'offeredCount'], 0);
    const newMessageCount = subscribe(accountNewMessages$);

    return (
        <div className="nike-links">
            {loggedIn && <MenuStatuses />}
            <MenuLinkDownloadApp />
            <MenuLink
                route={ACCOUNT_PROFILE}
                trackName="Moj profil"
                icon={UserProfilIcon}
                disabled={!loggedIn}
                label={i18n('menu.profil')}
                atid="main-menu-profile"
            />
            <MenuLink
                route={BONUS_DASHBOARD_CURRENT}
                trackName="Bonusy (Osobna zona)"
                icon={BonusIcon}
                disabled={!loggedIn}
                label={i18n('menu.bonusy')}
                badge={sumBonuses}
                atid="main-menu-bonuses"
            />
            <MenuLink
                route={MONEY_DEPOSIT}
                trackName="Vklad a vyber z uctu"
                icon={AccountIcon}
                disabled={!loggedIn}
                label={i18n('menu.money')}
                atid="main-menu-acconut"
            />
            <MenuLink
                route={ACCOUNT_SETTINGS}
                trackName="Nastavenia"
                icon={GearWheelIcon}
                disabled={!loggedIn}
                label={i18n('common.settings')}
                atid="main-menu-profile-settings"
            />
            <MenuLink
                route={MESSAGES}
                trackName="Spravy"
                icon={MessageIcon}
                label={i18n('menu.sprava')}
                badge={newMessageCount}
                disabled={!loggedIn}
                atid="main-menu-messages"
            />

            {loggedIn && isProfileOlympiaEnabled() ? (
                <MenuLink
                    route={TOURNAMENTS}
                    trackName={'Tournaments'}
                    icon={TournamentCupIcon}
                    label={i18n('tournaments.name')}
                    atid="main-menu-cl"
                    badge={unseenTournamentsCount}
                />
            ) : null}
            <MenuLink
                route={TICKET_CHECK}
                trackName="Kontrola tiketu"
                icon={TicketControllIcon}
                label={i18n('menu.kontrolatiketu')}
                atid="main-menu-ticket-control"
            />

            <MenuLink
                route={CLUB_NIKE}
                trackName="Klub Nike"
                icon={NIcon}
                label={i18n('menu.clubNike')}
                atid="main-menu-clubnike"
            />
            <MenuLink
                route={NIKE1_FAV}
                trackName="Oblubene"
                icon={FavoriteIcon}
                label={i18n('react.page.title.page:nikeOneFav')}
                disabled={!loggedIn}
                atid="main-menu-favorites"
            />
            <NotificationsMenuLink disabled={!loggedIn} />
            <MenuLink
                route={NIKE1_RESULTS}
                trackName="Vysledky"
                icon={ResultsIcon}
                label={i18n('react.page.title.vysledky')}
                atid="main-menu-results"
            />
            {loggedIn ? (
                <MenuLink
                    route={CASINO_HISTORY}
                    trackName="Historia hrania"
                    icon={SpinIcon}
                    label={i18n('casino.historia')}
                />
            ) : null}
            {loggedIn && isProfileIScratchCardEnabled() ? (
                <MenuLink
                    route={I_SCRATCH_CARDS_HISTORY}
                    trackName="Historia ižrebov"
                    icon={SpinIcon}
                    label={i18n('iscratch.card.history')}
                />
            ) : null}
            {loggedIn ? (
                <MenuLink
                    route={TRANSACTION_HISTORY}
                    trackName="Historia Transakcii"
                    icon={HistoryIcon}
                    label={i18n('menu.transactionHistory')}
                    disabled={!loggedIn}
                    atid="main-menu-transactions"
                />
            ) : null}

            <Languages />
            {loggedIn && (
                <div className="logout-main-menu">
                    <MenuLink
                        trackName="Odhlasenie"
                        icon={LogOutIcon}
                        onClick={logout}
                        label={i18n('menu.odhlasenie')}
                        atid="main-menu-logout"
                    />
                </div>
            )}
        </div>
    );
}

export default MainMenu;
